import React, { useCallback, useContext, useState } from 'react';
import { Button } from 'reactstrap';
import { GenericEntry } from '../..';
import { User } from '../../../../../entities/User';
import { SendEmailTrigger } from '../../../../../entities/applicant_tracking/Trigger';
import { Application } from '../../../../../entities/Application';
import {
  TriggerActions,
  TriggerExecutionStatus,
} from '../../../../../entities/ApplicationFeed';
import { AlertObject } from '../../../../../components/Alert';
import moment from 'moment';
import { CandidatePageContext } from '../../..';
import { EmailPreviewModal } from './EmailPreviewModal';
import { TriggerCancellationModal } from './TriggerCancellationModal';

interface PropTypes {
  application: Application;
  currentUser: User;
  entry: TriggerActions;
  isLastItemInPage: boolean;
  setAlert: (data: AlertObject) => void;
  reloadFeed: () => void;
}

interface ActionGroupProps {
  onCancel: () => void;
  onPreview: () => void;
}

const ActionGroup = (props: ActionGroupProps) => {
  const className = 'btn';

  const { canCancelTriggerEmail } = useContext(CandidatePageContext);

  return (
    <div className='d-flex gap-3 mt-2'>
      <Button color='secondary' className={className} onClick={props.onPreview}>
        Preview
      </Button>
      {canCancelTriggerEmail ? (
        <Button color='danger' className={className} onClick={props.onCancel}>
          Cancel
        </Button>
      ) : null}
    </div>
  );
};

export function TriggerEmailScheduled(props: PropTypes) {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isPreviewModalOpen, seIsPreviewModalOpen] = useState(false);
  const trigger = props.entry.trigger as SendEmailTrigger;
  const { scheduledExecuteAt, status } = props.entry.triggerExecution;

  const futureDate = moment(scheduledExecuteAt);
  const now = moment();

  let formattedDate: string | undefined = undefined;
  const isInFuture = futureDate.isAfter(now);
  const showsDate = isInFuture && status !== 'cancelled';

  if (showsDate) {
    const duration = moment.duration(futureDate.diff(now));
    formattedDate = `in ${duration.humanize()}`;
  }

  const handleOpenCancellationModal = useCallback(() => {
    setIsCancelModalOpen(true);
  }, [setIsCancelModalOpen]);

  const handleCancellationConfirm = useCallback(() => {
    setIsCancelModalOpen(false);
    props.reloadFeed();
  }, [setIsCancelModalOpen]);

  const handleOpenPreviewModal = useCallback(() => {
    seIsPreviewModalOpen(true);
  }, [seIsPreviewModalOpen]);

  const handleClosePreviewModal = useCallback(() => {
    seIsPreviewModalOpen(false);
  }, [seIsPreviewModalOpen]);

  const handleOnCloseCancellationModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, [setIsCancelModalOpen]);

  const handleCancelOnPreviewModal = useCallback(() => {
    seIsPreviewModalOpen(false);
    setIsCancelModalOpen(true);
  }, [setIsCancelModalOpen, setIsCancelModalOpen]);

  const statusComponentMap: Partial<Record<TriggerExecutionStatus, React.FC>> =
    {
      scheduled: () =>
        showsDate ? (
          <ActionGroup
            onCancel={handleOpenCancellationModal}
            onPreview={handleOpenPreviewModal}
          />
        ) : null,
    };

  const ComponentToRender = statusComponentMap[status] ?? (() => null);

  return (
    <>
      <GenericEntry
        icon='bi-envelope'
        iconClassName='bg-blue-gray-light'
        title={
          <>
            An automatic e-mail with the template{' '}
            <strong>{trigger.emailTemplateName}</strong> has been scheduled to
            be sent
            {showsDate ? <strong> {formattedDate}</strong> : null}.
          </>
        }
        date={props.entry.createdAt}
        {...props}
      >
        <ComponentToRender />
      </GenericEntry>
      <TriggerCancellationModal
        feedEntryId={props.entry.id}
        candidateName={props.application.candidate.name}
        isOpen={isCancelModalOpen}
        onClose={handleOnCloseCancellationModal}
        onConfirm={handleCancellationConfirm}
      />
      <EmailPreviewModal
        emailTemplateId={trigger.emailTemplateId}
        applicationId={props.application.id}
        isOpen={isPreviewModalOpen}
        onClose={handleClosePreviewModal}
        onCancel={handleCancelOnPreviewModal}
      />
    </>
  );
}
